import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { OgcFeaturesClient } from './ogc-features-client';
import { OgcTilesClient } from './ogc-tiles-client';
import {
  GPKG_COLLECTION_AIRPORTS,
  GPKG_COLLECTION_AIRSPACES,
  GPKG_COLLECTION_NAVAIDS,
  GPKG_COLLECTION_OBSTACLES,
  GPKG_COLLECTION_FIXES,
  GPKG_COLLECTION_TFRS,
  GPKG_COLLECTION_FIR_BOUNDARIES,
  GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET,
  GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER,
  GPKG_RASTER_TILE_TAC_DATASET,
  GPKG_RASTER_TILE_TAC_LAYER,
  GPKG_RASTER_TILE_HELI_ROUTE_DATASET,
  GPKG_RASTER_TILE_HELI_ROUTE_LAYER,
  GPKG_RASTER_TILE_IFR_LOW_DATASET,
  GPKG_RASTER_TILE_IFR_LOW_LAYER,
  GPKG_RASTER_TILE_IFR_HIGH_DATASET,
  GPKG_RASTER_TILE_IFR_HIGH_LAYER,
  GPKG_RASTER_TILE_IFR_AREA_DATASET,
  GPKG_RASTER_TILE_IFR_AREA_LAYER,
  GPKG_VECTOR_TILE_AIRPORTS_DATASET,
  GPKG_VECTOR_TILE_AIRPORTS_LAYER,
  GPKG_VECTOR_TILE_NAVAIDS_DATASET,
  GPKG_VECTOR_TILE_NAVAIDS_LAYER,
  GPKG_VECTOR_TILE_OBSTACLES_DATASET,
  GPKG_VECTOR_TILE_OBSTACLES_LAYER,
  GPKG_VECTOR_TILE_AIRSPACES_DATASET,
  GPKG_VECTOR_TILE_AIRSPACES_LAYER,
  GPKG_VECTOR_TILE_AIRWAYS_DATASET,
  GPKG_VECTOR_TILE_AIRWAYS_LAYER,
  buildChartOgcCollectionUrl,
  buildOgcCollectionUrl,
  buildVectorTileUrl,
  buildXyzTileUrl,
  buildTerrainTileUrl,
} from '../config/environment';

// ─── Endpoint configuration ─────────────────────────────────────────────────
// Defaults are resolved from the `config/environment.ts` module so they
// respect `VITE_CHART_API_BASE` / `VITE_GPKG_SERVER_BASE` at build time.
export interface TileEndpoints {
  /** VFR Sectional XYZ raster tiles */
  vfrSectional: string;
  /** TAC XYZ raster tiles */
  tac: string;
  /** IFR Low Enroute XYZ raster tiles */
  ifrLow: string;
  /** IFR High Enroute XYZ raster tiles */
  ifrHigh: string;
  /** IFR Area XYZ raster tiles */
  ifrArea: string;
  /** Helicopter Route XYZ raster tiles */
  heliRoute: string;
  /** NEXRAD weather radar XYZ tiles */
  nexrad: string;
  /** Terrain DEM (Terrarium encoding) */
  terrainDem: string;
}

export interface FeatureEndpoints {
  /** OGC API Features base URL for airports */
  airports: string;
  /** OGC API Features base URL for airspaces */
  airspaces: string;
  /** OGC API Features base URL for navaids */
  navaids: string;
  /** OGC API Features base URL for obstacles */
  obstacles: string;
  /** OGC API Features base URL for reporting points / fixes */
  fixes: string;
  /** OGC API Features base URL for TFRs */
  tfrs: string;
  /** OGC API Features base URL for FIR/UIR boundaries */
  firBoundaries: string;
  /** OGC API Features base URL for d-TPP approach-plate quads */
  approachPlates: string;
}

export interface VectorTileEndpoints {
  /** OGC API Tiles (MVT) for airspace polygons */
  airspaces?: string;
  /** OGC API Tiles (MVT) for airport points */
  airports?: string;
  /** OGC API Tiles (MVT) for obstacles */
  obstacles?: string;
  /** OGC API Tiles (MVT) for navaids */
  navaids?: string;
  /** OGC API Tiles (MVT) for routes / airways */
  airways?: string;
}

// ─── Default endpoints (resolved from environment config) ───────────────────
// Raster charts + terrain DEM come from the `aviation` dataset convention
// (gpkg_server scans DATA_DIR/aviation/*.gpkg|*.mbtiles and exposes
// `aviation-<layer>` IDs). Operators can override any of these via props on
// <OgcServicesProvider> (e.g. in tests) or via env vars at build time.
const DEFAULT_TILE_ENDPOINTS: TileEndpoints = {
  vfrSectional: buildXyzTileUrl(GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET, GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER),
  tac:          buildXyzTileUrl(GPKG_RASTER_TILE_TAC_DATASET, GPKG_RASTER_TILE_TAC_LAYER),
  ifrLow:       buildXyzTileUrl(GPKG_RASTER_TILE_IFR_LOW_DATASET, GPKG_RASTER_TILE_IFR_LOW_LAYER),
  ifrHigh:      buildXyzTileUrl(GPKG_RASTER_TILE_IFR_HIGH_DATASET, GPKG_RASTER_TILE_IFR_HIGH_LAYER),
  ifrArea:      buildXyzTileUrl(GPKG_RASTER_TILE_IFR_AREA_DATASET, GPKG_RASTER_TILE_IFR_AREA_LAYER),
  heliRoute:    buildXyzTileUrl(GPKG_RASTER_TILE_HELI_ROUTE_DATASET, GPKG_RASTER_TILE_HELI_ROUTE_LAYER),
  nexrad:       buildXyzTileUrl('weather', 'nexrad'),
  terrainDem:   buildTerrainTileUrl('elevation', 'dem', 'terrarium'),
};

const DEFAULT_FEATURE_ENDPOINTS: FeatureEndpoints = {
  airports:       buildOgcCollectionUrl(GPKG_COLLECTION_AIRPORTS),
  airspaces:      buildOgcCollectionUrl(GPKG_COLLECTION_AIRSPACES),
  navaids:        buildOgcCollectionUrl(GPKG_COLLECTION_NAVAIDS),
  obstacles:      buildOgcCollectionUrl(GPKG_COLLECTION_OBSTACLES),
  fixes:          buildOgcCollectionUrl(GPKG_COLLECTION_FIXES),
  tfrs:           buildOgcCollectionUrl(GPKG_COLLECTION_TFRS),
  firBoundaries:  buildOgcCollectionUrl(GPKG_COLLECTION_FIR_BOUNDARIES),
  // approach plates come from chart_api (separate service), keyed through ENDPOINTS
  approachPlates: buildChartOgcCollectionUrl('approach_plates'),
};

const DEFAULT_VECTOR_TILE_ENDPOINTS: VectorTileEndpoints = {
  airspaces: GPKG_VECTOR_TILE_AIRSPACES_DATASET && GPKG_VECTOR_TILE_AIRSPACES_LAYER
    ? buildVectorTileUrl(GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER)
    : undefined,
  airports:  buildVectorTileUrl(GPKG_VECTOR_TILE_AIRPORTS_DATASET, GPKG_VECTOR_TILE_AIRPORTS_LAYER),
  obstacles: buildVectorTileUrl(GPKG_VECTOR_TILE_OBSTACLES_DATASET, GPKG_VECTOR_TILE_OBSTACLES_LAYER),
  navaids:   buildVectorTileUrl(GPKG_VECTOR_TILE_NAVAIDS_DATASET, GPKG_VECTOR_TILE_NAVAIDS_LAYER),
  airways:   GPKG_VECTOR_TILE_AIRWAYS_DATASET && GPKG_VECTOR_TILE_AIRWAYS_LAYER
    ? buildVectorTileUrl(GPKG_VECTOR_TILE_AIRWAYS_DATASET, GPKG_VECTOR_TILE_AIRWAYS_LAYER)
    : undefined,
};

interface OgcServicesContextValue {
  tileEndpoints: TileEndpoints;
  featureEndpoints: FeatureEndpoints;
  vectorTileEndpoints: VectorTileEndpoints;
  featuresClient: OgcFeaturesClient;
  tilesClient: OgcTilesClient;
}

const OgcServicesContext = createContext<OgcServicesContextValue>(null!);
export const useOgcServices = () => useContext(OgcServicesContext);

interface Props {
  tileEndpoints?: Partial<TileEndpoints>;
  featureEndpoints?: Partial<FeatureEndpoints>;
  vectorTileEndpoints?: Partial<VectorTileEndpoints>;
  children: ReactNode;
}

export const OgcServicesProvider: React.FC<Props> = ({
  tileEndpoints: tileProp,
  featureEndpoints: featProp,
  vectorTileEndpoints: vecProp,
  children,
}) => {
  const value = useMemo<OgcServicesContextValue>(() => {
    const tiles = { ...DEFAULT_TILE_ENDPOINTS, ...tileProp };
    const features = { ...DEFAULT_FEATURE_ENDPOINTS, ...featProp };
    const vectors = { ...DEFAULT_VECTOR_TILE_ENDPOINTS, ...vecProp };
    return {
      tileEndpoints: tiles,
      featureEndpoints: features,
      vectorTileEndpoints: vectors,
      featuresClient: new OgcFeaturesClient(features),
      tilesClient: new OgcTilesClient(tiles, vectors),
    };
  }, [tileProp, featProp, vecProp]);

  return <OgcServicesContext.Provider value={value}>{children}</OgcServicesContext.Provider>;
};
